/** Inbox networks on Engage Connect. Social still publishes Threads and TikTok posts. */
export const ENGAGE_INBOX_NETWORKS = [
	"whatsapp",
	"telegram",
	"messenger",
	"instagram",
	"threads",
	"x",
	"tiktok",
	"linkedin",
] as const;
export type EngageInboxNetwork = (typeof ENGAGE_INBOX_NETWORKS)[number];

/** Icon grid on Connect Chat Accounts, in the order the product lists them. */
export const ENGAGE_CONNECT_NETWORKS = ENGAGE_INBOX_NETWORKS;
export type EngageConnectNetwork = EngageInboxNetwork;

/**
 * Connector process names. Bridge source stays out of this repository.
 * Instagram is a separate runtime from Messenger even when they share an
 * upstream repo. TikTok has no vetted personal-DM bridge.
 * @see https://github.com/beeper
 * @see https://github.com/mautrix/whatsapp
 */
export const ENGAGE_BRIDGE_IDS = {
	whatsapp: "whatsapp",
	telegram: "telegram",
	messenger: "meta",
	instagram: "instagram",
	threads: "threads",
	x: "twitter",
	tiktok: "tiktok",
	linkedin: "linkedin",
} as const satisfies Record<EngageInboxNetwork, string>;

/**
 * Official HTTP send for Page/professional APIs. WhatsApp Cloud API is not
 * in this list: WhatsApp traffic uses Linked Devices only.
 */
export const ENGAGE_LIVE_NETWORKS = ["messenger", "instagram"] as const;
export type EngageLiveNetwork = (typeof ENGAGE_LIVE_NETWORKS)[number];

/** Official Page/professional APIs only. Linked Devices WhatsApp has no 24-hour lock. */
export const ENGAGE_REPLY_WINDOW_NETWORKS = ["messenger", "instagram"] as const;

export const ENGAGE_DIRECTIONS = ["inbound", "outbound"] as const;
export type EngageDirection = (typeof ENGAGE_DIRECTIONS)[number];

export const ENGAGE_CONVERSATION_STATUSES = ["open", "closed"] as const;
export type EngageConversationStatus = (typeof ENGAGE_CONVERSATION_STATUSES)[number];

export const ENGAGE_CONVERSATION_KINDS = ["dm", "group"] as const;
export type EngageConversationKind = (typeof ENGAGE_CONVERSATION_KINDS)[number];

export interface EngageParticipant {
	providerId: string;
	displayName: string;
	phone?: string;
	username?: string;
}

export const ENGAGE_TEXT_MARKS = ["italic", "bold", "underline", "strike", "code"] as const;
export type EngageTextMark = (typeof ENGAGE_TEXT_MARKS)[number];

export interface EngageTextEntity {
	offset: number;
	length: number;
	type: EngageTextMark;
}

/** WhatsApp group JIDs end in @g.us. Direct chats use @s.whatsapp.net or @lid. */
export function engageConversationKindFromJid(jid: string): EngageConversationKind {
	return jid.endsWith("@g.us") ? "group" : "dm";
}

/** Company LinkedIn login is an email address, not a phone. */
export function engageLooksLikeEmail(value: string): boolean {
	const trimmed = value.trim();
	if (!trimmed || trimmed.length > 254) return false;
	return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
}

/** Raw WhatsApp ids and digits are not a saved contact name. */
export function engageLooksLikeProviderId(value: string): boolean {
	const trimmed = value.trim();
	if (!trimmed) return false;
	if (trimmed.includes("@")) return true;
	if (/^\+?\d{6,}$/.test(trimmed)) return true;
	// WhatsApp hides a number it will not share as "+60∙∙∙∙∙∙∙10". That is not a name.
	return /^\+?[\d\s∙•·*.-]{6,}$/.test(trimmed) && /[∙•·*]/.test(trimmed);
}

/** WhatsApp Linked IDs are not MSISDNs and must not be shown as +numbers. */
export function engageLooksLikeWhatsAppLid(value: string): boolean {
	const trimmed = value.trim();
	if (!trimmed) return false;
	const host = trimmed.includes("@") ? (trimmed.split("@")[1] ?? "") : "";
	if (host === "lid" || host.endsWith(".lid")) return true;
	const digits = (trimmed.split("@")[0] ?? "").replace(/\D/g, "");
	return !host && digits.length >= 14;
}

/**
 * Real WhatsApp MSISDN from a JID or digits. Linked IDs and group JIDs
 * are not phone numbers.
 */
export function engageWhatsAppPhone(value: string): string {
	const trimmed = value.trim();
	if (!trimmed || engageLooksLikeWhatsAppLid(trimmed)) return "";
	const host = trimmed.includes("@") ? (trimmed.split("@")[1] ?? "") : "";
	if (host === "g.us") return "";
	if (/^\+\d{6,13}$/.test(trimmed)) return trimmed;
	const local = (trimmed.split("@")[0] ?? "").split(":")[0] ?? "";
	const digits = local.replace(/\D/g, "");
	if (digits.length < 6 || digits.length > 13) return "";
	return `+${digits}`;
}

/** Photos stay in albums. Everything else is a downloadable file. */
export function engageLooksLikeImageMime(value: string | null | undefined): boolean {
	return (value ?? "").trim().toLowerCase().startsWith("image/");
}

/** Telegram's `User.phone` only. Never a Telegram user id. */
export function engageTelegramPhone(value: string | null | undefined): string {
	const digits = (value ?? "").replace(/\D/g, "");
	if (digits.length < 8 || digits.length > 15) return "";
	return `+${digits}`;
}

/**
 * Telegram user login needs an E.164 number. Local numbers such as 012…
 * are rejected so the connector does not guess the country.
 */
export function normalizeEngageLoginPhone(input: string): string | null {
	const compact = input.trim().replace(/[\s().-]/g, "");
	if (!compact) return null;
	let next = compact.startsWith("00") ? `+${compact.slice(2)}` : compact;
	if (/^[1-9]\d{9,14}$/.test(next)) next = `+${next}`;
	if (!/^\+[1-9]\d{7,14}$/.test(next)) return null;
	return next;
}

export function engageLoginPhoneHint(input: string): string {
	const raw = input.trim();
	if (!raw) return "Enter the number with country code, like +60 12 345 6789.";
	if (normalizeEngageLoginPhone(raw)) return "";
	const digits = raw.replace(/\D/g, "");
	if (digits.startsWith("0")) return "Drop the leading 0 and add the country code, like +60 12 345 6789.";
	return "Use an international number with country code, like +60 12 345 6789.";
}

/** Saved address-book name. Not a JID, Linked ID, or bare phone number. */
export function engageIsSavedContactName(value: string): boolean {
	const name = value.trim();
	if (!name) return false;
	return !engageLooksLikeProviderId(name) && !engageLooksLikeWhatsAppLid(name);
}

/** A trusted address-book name replaces an empty, raw, or earlier guess. */
export function engageBetterCustomerName(next: string, current: string, trusted = false): boolean {
	if (!engageIsSavedContactName(next) || next.trim() === current.trim()) return false;
	if (!engageIsSavedContactName(current)) return true;
	if (/\s/.test(current.trim()) && !/\s/.test(next.trim())) return false;
	return trusted;
}

/** Saved address-book name, otherwise the real number. Never a profile push name. */
export function engageConversationTitle(input: {
	customerName: string;
	customerHandle?: string;
	kind?: EngageConversationKind | string;
}): string {
	const name = input.customerName.trim();
	if (input.kind === "group") return name || "Group";
	const phone = engageWhatsAppPhone(input.customerHandle ?? "") || engageWhatsAppPhone(name);
	if (engageIsSavedContactName(name)) return name;
	if (engageLooksLikeWhatsAppLid(name) || engageLooksLikeWhatsAppLid(input.customerHandle ?? "")) {
		return phone;
	}
	if (!name || engageLooksLikeProviderId(name)) return phone || name;
	return name;
}

/** Group member or group-message sender. Saved name, then number. Never a Linked ID. */
export function engagePersonLabel(input: { displayName?: string | null; providerId?: string | null }): string {
	return engageConversationTitle({
		customerName: input.displayName ?? "",
		customerHandle: input.providerId ?? "",
		kind: "dm",
	});
}

/** Telegram group last-line "— reply by Ilhan" is the sender, not part of the text. */
export function engageSplitReplyBy(body: string): { body: string; replyBy: string } {
	const match = body.match(/\s*—\s*reply by\s+(.+?)\s*$/i);
	if (!match || match.index == null) return { body, replyBy: "" };
	return { body: body.slice(0, match.index).trimEnd(), replyBy: (match[1] ?? "").trim() };
}

export function engageVisibleMessageBody(body: string): string {
	return engageSplitReplyBy(body).body;
}

/** Telegram sent italic/bold. Offsets are UTF-16, same as a JS string. */
export function parseTelegramTextEntities(raw: unknown, text: string): EngageTextEntity[] {
	if (!text || !Array.isArray(raw)) return [];
	const found: EngageTextEntity[] = [];
	for (const item of raw) {
		if (!item || typeof item !== "object") continue;
		const row = item as Record<string, unknown>;
		const type = telegramTextMark(row);
		const offset = Number(row.offset);
		const length = Number(row.length);
		if (!type || !Number.isInteger(offset) || !Number.isInteger(length) || offset < 0 || length <= 0) continue;
		if (offset >= text.length) continue;
		found.push({ offset, length: Math.min(length, text.length - offset), type });
	}
	return found;
}

export function shiftEngageTextEntities(entities: EngageTextEntity[], raw: string, next: string): EngageTextEntity[] {
	const lead = raw.length - raw.trimStart().length;
	return entities
		.map((entity) => ({ ...entity, offset: entity.offset - lead }))
		.filter((entity) => entity.offset + entity.length > 0 && entity.offset < next.length)
		.map((entity) => {
			const offset = Math.max(0, entity.offset);
			return { ...entity, offset, length: Math.min(entity.length, next.length - offset) };
		})
		.filter((entity) => entity.length > 0);
}

/** Ops chats italicise "— reply by Name". Keep that look when Telegram hid the entities. */
export function engageImpliedTextEntities(body: string): EngageTextEntity[] {
	const match = body.match(/—\s*reply by\s+\S[\s\S]*$/i);
	if (!match || match.index == null) return [];
	return [{ offset: match.index, length: match[0].length, type: "italic" }];
}

export function engageDisplayTextEntities(body: string, entities?: EngageTextEntity[] | null): EngageTextEntity[] {
	return entities?.length ? entities : engageImpliedTextEntities(body);
}

export function engageFormatText(
	body: string,
	entities?: EngageTextEntity[] | null,
): Array<{ text: string; marks: EngageTextMark[] }> {
	const marks = engageDisplayTextEntities(body, entities);
	if (!body) return [];
	if (!marks.length) return [{ text: body, marks: [] }];
	const bounds = new Set<number>([0, body.length]);
	for (const entity of marks) {
		bounds.add(Math.max(0, Math.min(body.length, entity.offset)));
		bounds.add(Math.max(0, Math.min(body.length, entity.offset + entity.length)));
	}
	const points = [...bounds].sort((left, right) => left - right);
	const parts: Array<{ text: string; marks: EngageTextMark[] }> = [];
	for (let index = 0; index < points.length - 1; index += 1) {
		const start = points[index] ?? 0;
		const end = points[index + 1] ?? start;
		if (end <= start) continue;
		const active = ENGAGE_TEXT_MARKS.filter((mark) =>
			marks.some((entity) => entity.type === mark && start >= entity.offset && start < entity.offset + entity.length),
		);
		parts.push({ text: body.slice(start, end), marks: active });
	}
	return parts.length ? parts : [{ text: body, marks: [] }];
}

const ENGAGE_HTTP_LINK = /\b(?:https?:\/\/|www\.)[^\s<>"'`]+/gi;

/** Only http(s). javascript: and other schemes stay plain text. */
export function engageSafeHttpUrl(raw: string): string {
	const trimmed = raw.trim();
	if (!trimmed) return "";
	const candidate = /^www\./i.test(trimmed) ? `https://${trimmed}` : trimmed;
	try {
		const parsed = new URL(candidate);
		if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return "";
		if (!parsed.hostname.includes(".")) return "";
		return parsed.href;
	} catch {
		return "";
	}
}

function peelEngageLink(raw: string): { core: string; tail: string } {
	let core = raw;
	let tail = "";
	while (core) {
		const last = core.at(-1) ?? "";
		if (!/[),.;:!?']/.test(last)) break;
		const opens = core.match(/\(/g)?.length ?? 0;
		const closes = core.match(/\)/g)?.length ?? 0;
		if (last === ")" && opens >= closes) break;
		core = core.slice(0, -1);
		tail = last + tail;
	}
	return { core, tail };
}

/** Split a bubble so visible http(s) and www. addresses can open. */
export function engageSplitHttpLinks(text: string): Array<{ text: string; href?: string }> {
	if (!text) return [];
	const parts: Array<{ text: string; href?: string }> = [];
	let cursor = 0;
	for (const match of text.matchAll(new RegExp(ENGAGE_HTTP_LINK.source, "gi"))) {
		const start = match.index ?? 0;
		if (start < cursor) continue;
		const peeled = peelEngageLink(match[0] ?? "");
		const href = engageSafeHttpUrl(peeled.core);
		if (!href) continue;
		if (start > cursor) parts.push({ text: text.slice(cursor, start) });
		parts.push({ text: peeled.core, href });
		cursor = start + peeled.core.length;
	}
	if (cursor < text.length) parts.push({ text: text.slice(cursor) });
	return parts.length ? parts : [{ text }];
}

function telegramTextMark(row: Record<string, unknown>): EngageTextMark | "" {
	const name = String(row.className ?? row.classType ?? row._ ?? "").toLowerCase();
	if (name.includes("italic")) return "italic";
	if (name.includes("bold")) return "bold";
	if (name.includes("underline")) return "underline";
	if (name.includes("strike")) return "strike";
	if (name.includes("pre") || name.includes("code")) return "code";
	return "";
}

/** Telegram Web search: every word must match, punctuation is ignored. */
export function engageInboxMatchesQuery(haystack: string, query: string): boolean {
	const tokens = engageInboxSearchText(query).split(" ").filter(Boolean);
	if (tokens.length === 0) return true;
	const hay = engageInboxSearchText(haystack);
	return tokens.every((token) => hay.includes(token));
}

export function engageInboxSearchText(value: string): string {
	return value
		.toLowerCase()
		.replace(/[^\p{L}\p{N}]+/gu, " ")
		.trim();
}

/** Same Telegram group can arrive as 123 and -100123. Keep one inbox row. */
export function engageTelegramInboxKey(input: {
	network?: string;
	kind?: EngageConversationKind | string;
	customerHandle?: string;
}): string {
	if (input.network && input.network !== "telegram") return "";
	const raw = (input.customerHandle ?? "").trim();
	if (!raw) return "";
	const digits = raw.replace(/\D/g, "");
	if (!digits) return "";
	const id = digits.startsWith("100") && digits.length >= 12 ? digits.slice(3) : digits;
	return `telegram:${input.kind === "group" ? "group" : "dm"}:${id}`;
}

/** Same WhatsApp person can appear as a phone JID and a Linked ID. Keep one. */
export function engageWhatsAppPersonKey(input: {
	network?: string;
	kind?: EngageConversationKind | string;
	customerHandle?: string;
	customerPhone?: string;
}): string {
	if (input.network && input.network !== "whatsapp") return "";
	if (input.kind === "group") return "";
	return engageWhatsAppPhone(input.customerPhone ?? "") || engageWhatsAppPhone(input.customerHandle ?? "");
}

/** One inbox row per WhatsApp person or group JID. Same-name groups stay separate. */
export function engageWhatsAppInboxKey(input: {
	network?: string;
	kind?: EngageConversationKind | string;
	customerHandle?: string;
	customerPhone?: string;
}): string {
	if (input.network && input.network !== "whatsapp") return "";
	if (input.kind === "group") {
		const handle = (input.customerHandle ?? "").trim().toLowerCase();
		return handle.endsWith("@g.us") ? `group:${handle}` : "";
	}
	const phone = engageWhatsAppPersonKey(input);
	if (phone) return `dm:${phone}`;
	const handle = input.customerHandle ?? "";
	if (!engageLooksLikeWhatsAppLid(handle)) return "";
	const lid = (handle.split("@")[0] ?? "").split(":")[0] ?? "";
	return lid ? `dm:lid:${lid}` : "";
}

/**
 * A chat removed on the phone matches every Engage row for that group JID,
 * Linked ID, or phone number. A phone delete must remove the Linked ID copy too.
 */
export function engageChatDeletionMatches(input: {
	network?: string;
	kind?: string;
	customerHandle?: string;
	providerConversationId?: string | null;
	customerPhone?: string | null;
	deletedId: string;
	deletedPhone?: string;
}): boolean {
	const handles = [input.providerConversationId, input.customerHandle].filter(
		(value): value is string => Boolean(value),
	);
	if (handles.some((handle) => handle === input.deletedId)) return true;
	if (input.kind === "group" || input.deletedId.endsWith("@g.us")) return false;
	if (input.network && input.network !== "whatsapp") return false;
	const incoming = engageWhatsAppPhone(input.deletedPhone ?? "") || engageWhatsAppPhone(input.deletedId);
	if (incoming) {
		const key = engageWhatsAppPersonKey({
			network: "whatsapp",
			kind: input.kind,
			customerHandle: input.customerHandle || input.providerConversationId || "",
			customerPhone: input.customerPhone ?? undefined,
		});
		if (key && key === incoming) return true;
	}
	if (!engageLooksLikeWhatsAppLid(input.deletedId)) return false;
	const lid = (input.deletedId.split("@")[0] ?? "").split(":")[0] ?? "";
	if (!lid) return false;
	return handles.some((handle) => {
		if (!engageLooksLikeWhatsAppLid(handle)) return false;
		return (handle.split("@")[0] ?? "").split(":")[0] === lid;
	});
}

export function engageUniqueInboxRows<T extends {
	id: string;
	network?: string;
	kind?: string;
	customerHandle?: string;
	customerPhone?: string;
	lastMessage?: string | null;
	lastMessageAt?: string | null;
	participants?: EngageParticipant[];
}>(rows: T[]): T[] {
	const unique: T[] = [];
	const byKey = new Map<string, T>();
	for (const row of rows) {
		const key = engageWhatsAppInboxKey(row) || engageTelegramInboxKey(row);
		if (!key) {
			unique.push(row);
			continue;
		}
		const current = byKey.get(key);
		if (!current) {
			byKey.set(key, row);
			continue;
		}
		const preferred = engagePreferWhatsAppConversation(current, row);
		byKey.set(key, {
			...preferred,
			participants: mergeEngageInboxParticipants(current.participants, row.participants),
		});
	}
	unique.push(...byKey.values());
	return unique;
}

export function mergeEngageInboxParticipants<T extends EngageParticipant>(
	left?: T[] | null,
	right?: T[] | null,
): T[] {
	const byKey = new Map<string, T>();
	for (const person of [...(left ?? []), ...(right ?? [])]) {
		const key = person.providerId.replace(/\D/g, "") || person.providerId;
		if (!key) continue;
		const existing = byKey.get(key);
		if (!existing) {
			byKey.set(key, person);
			continue;
		}
		const nextNamed = Boolean(person.displayName.trim() && !/^\d+$/.test(person.displayName.trim()));
		const currentNamed = Boolean(existing.displayName.trim() && !/^\d+$/.test(existing.displayName.trim()));
		byKey.set(key, {
			...(nextNamed || !currentNamed ? person : existing),
			phone: person.phone || existing.phone,
			username: person.username || existing.username,
		});
	}
	return [...byKey.values()];
}

export function engagePreferWhatsAppConversation<T extends {
	customerHandle?: string;
	lastMessage?: string | null;
	lastMessageAt?: string | null;
}>(current: T, next: T): T {
	const rank = (row: T) =>
		(engageLooksLikeWhatsAppLid(row.customerHandle ?? "") ? 0 : 2) + (row.lastMessage?.trim() ? 4 : 0);
	const nextRank = rank(next);
	const currentRank = rank(current);
	if (nextRank !== currentRank) return nextRank > currentRank ? next : current;
	return (next.lastMessageAt ?? "") > (current.lastMessageAt ?? "") ? next : current;
}

/** Inbox order follows the last stored message, not a history-sync clock. */
export function engageListTime(
	latestMessageAt?: string | Date | null,
	storedLastMessageAt?: string | Date | null,
): string | null {
	return engageIsoTime(latestMessageAt) ?? engageIsoTime(storedLastMessageAt);
}

function engageIsoTime(value?: string | Date | null): string | null {
	if (!value) return null;
	if (value instanceof Date) {
		if (Number.isNaN(value.getTime()) || value.getTime() <= 0) return null;
		return value.toISOString();
	}
	const trimmed = value.trim();
	return trimmed || null;
}

/**
 * Inbox rows follow the live link. Disconnected or expired accounts must not
 * keep showing stored chats.
 */
export function engageAccountShowsInbox(status: string): boolean {
	return status === "active" || status === "syncing" || status === "degraded" || status === "connecting";
}

/** History sync must not block an already-linked WhatsApp send. */
export function engageAccountCanSend(status: string): boolean {
	return status === "active" || status === "syncing" || status === "degraded";
}

/** Same phone, including device suffixes like 6012:3@s.whatsapp.net. */
export function engageWhatsAppAccountKey(providerAccountId: string): string {
	return engageWhatsAppPhone(providerAccountId);
}

/** Keep the live Linked Devices row when the same WhatsApp number appears twice. */
export function engagePreferLinkedAccount<T extends { status: string; lastSyncAt?: string | Date | null }>(
	current: T,
	next: T,
): T {
	const rank = (status: string) =>
		status === "active" ? 5 : status === "syncing" ? 4 : status === "degraded" ? 3 : status === "connecting" ? 2 : 0;
	const nextRank = rank(next.status);
	const currentRank = rank(current.status);
	if (nextRank !== currentRank) return nextRank > currentRank ? next : current;
	const stamp = (value: string | Date | null | undefined) =>
		value instanceof Date ? value.toISOString() : (value ?? "");
	return stamp(next.lastSyncAt) > stamp(current.lastSyncAt) ? next : current;
}

export const ENGAGE_ACCOUNT_STATUSES = [
	"pending",
	"connecting",
	"active",
	"syncing",
	"degraded",
	"disconnected",
	"expired",
] as const;
export type EngageAccountStatus = (typeof ENGAGE_ACCOUNT_STATUSES)[number];

export const ENGAGE_MESSAGE_STATUSES = [
	"queued",
	"sending",
	"sent",
	"delivered",
	"read",
	"failed",
	"unsupported",
] as const;
export type EngageMessageStatus = (typeof ENGAGE_MESSAGE_STATUSES)[number];

export const ENGAGE_MESSAGE_TYPES = ["text", "media", "unsupported"] as const;
export type EngageMessageType = (typeof ENGAGE_MESSAGE_TYPES)[number];

export const ENGAGE_LOCATION_MIME = "engage/location";
export const ENGAGE_CONTACT_MIME = "engage/contact";
export const ENGAGE_POLL_MIME = "engage/poll";

export function engageIsPlaceholderBody(body: string | null | undefined): boolean {
	const text = (body ?? "").trim();
	return (
		text === "This message type is not supported in Key Engage." ||
		text === "This attachment is not shown in Key Engage yet."
	);
}

export const ENGAGE_ACCOUNT_ROLES = ["owner", "admin", "agent"] as const;
export type EngageAccountRole = (typeof ENGAGE_ACCOUNT_ROLES)[number];

export const ENGAGE_ACCOUNT_PERMISSIONS = [
	"account:read",
	"account:reply",
	"account:notes",
	"account:members",
	"account:disconnect",
] as const;
export type EngageAccountPermission = (typeof ENGAGE_ACCOUNT_PERMISSIONS)[number];

export const ENGAGE_ACCOUNT_ROLE_PERMISSIONS: Record<EngageAccountRole, readonly EngageAccountPermission[]> = {
	owner: ["account:read", "account:reply", "account:notes", "account:members", "account:disconnect"],
	admin: ["account:read", "account:reply", "account:notes", "account:members"],
	agent: ["account:read", "account:reply", "account:notes"],
};

export function engageAccountRoleHas(
	role: EngageAccountRole,
	permission: EngageAccountPermission,
): boolean {
	return ENGAGE_ACCOUNT_ROLE_PERMISSIONS[role].includes(permission);
}

/**
 * Exact meanings for UI, code and the platform matrix. An OAuth 200, a QR
 * image, a database row or a unit test is not WORKING.
 */
export const ENGAGE_INTEGRATION_STATUSES = [
	"WORKING",
	"PARTIAL",
	"SETUP_REQUIRED",
	"EXPERIMENTAL",
	"WEB_ONLY",
	"BLOCKED",
	"UNSUPPORTED",
	"NOT_TESTED",
] as const;
export type EngageIntegrationStatus = (typeof ENGAGE_INTEGRATION_STATUSES)[number];

export const ENGAGE_TRANSPORTS = [
	"whatsapp_web",
	"telegram_mtproto",
	"linkedin_voyager",
	"meta_page",
	"web_only",
	"none",
] as const;
export type EngageTransport = (typeof ENGAGE_TRANSPORTS)[number];

const WINDOW_MS = 24 * 60 * 60 * 1000;

export interface EngageAccount {
	id: string;
	organisationId: string;
	network: EngageInboxNetwork;
	displayName: string;
	nickname: string | null;
	providerAccountId: string;
	connectorId: string | null;
	status: EngageAccountStatus;
	connectedAt: Date;
	disconnectedAt: Date | null;
	lastSyncAt: Date | null;
	lastErrorAt: Date | null;
	linkedByUserId: string | null;
	consentAcknowledgedAt: Date | null;
	ownerUserId: string | null;
	agentScope: "all" | "assigned";
}

export interface EngageAccountMember {
	id: string;
	organisationId: string;
	accountId: string;
	userId: string;
	role: EngageAccountRole;
	permissions: EngageAccountPermission[];
	createdAt: Date;
}

export interface EngageCustomer {
	id: string;
	organisationId: string;
	network: EngageInboxNetwork;
	providerCustomerId: string;
	displayName: string;
}

export interface EngageConversation {
	id: string;
	organisationId: string;
	accountId: string;
	customerId: string;
	status: EngageConversationStatus;
	assigneeId: string | null;
	lastCustomerMessageAt: Date | null;
	lastMessageAt: Date | null;
	deletedAt: Date | null;
}

export interface EngageMessage {
	id: string;
	organisationId: string;
	conversationId: string;
	direction: EngageDirection;
	body: string;
	providerMessageId: string | null;
	sendStatus: EngageMessageStatus | null;
	messageType: EngageMessageType;
	createdAt: Date;
	editedAt: Date | null;
	deletedAt: Date | null;
	replyTo?: EngageReplyQuote | null;
}

export interface EngageReplyQuote {
	messageId?: string | null;
	providerMessageId?: string | null;
	name?: string | null;
	body?: string | null;
}

/** WhatsApp Web: own text can be edited for 15 minutes. */
export const ENGAGE_WHATSAPP_EDIT_MS = 15 * 60 * 1000;
/** WhatsApp Web: delete for everyone stays open for about two days. */
export const ENGAGE_WHATSAPP_DELETE_MS = 48 * 60 * 60 * 1000;
/** Telegram: own messages can be edited for two days. */
export const ENGAGE_TELEGRAM_EDIT_MS = 48 * 60 * 60 * 1000;
/** Telegram: delete for everyone stays open for two days. */
export const ENGAGE_TELEGRAM_DELETE_MS = 48 * 60 * 60 * 1000;

/** Telegram inbound ids are `peer:id`. Sends used to store only `id`. */
export function engageTelegramNumericId(value: string | null | undefined): string {
	if (!value) return "";
	const text = value.trim();
	const tail = text.includes(":") ? (text.split(":").pop() ?? "") : text;
	return /^-?\d+$/.test(tail) ? tail : "";
}

export function engageTelegramMessageId(peer: string, id: string | number): string {
	const numeric = engageTelegramNumericId(String(id));
	return peer && numeric ? `${peer}:${numeric}` : numeric;
}

export function engageMessageDeletedCopy(): string {
	return "This message was deleted";
}

export function canEditEngageMessage(input: {
	direction: string;
	messageType?: string | null;
	deletedAt?: Date | string | null;
	createdAt: Date | string;
	sendStatus?: string | null;
	network?: string | null;
	now?: Date;
}): boolean {
	if (input.network && input.network !== "whatsapp" && input.network !== "telegram") return false;
	if (input.direction !== "outbound" || input.deletedAt) return false;
	if (input.messageType && input.messageType !== "text") return false;
	if (input.sendStatus === "failed" || input.sendStatus === "queued" || input.sendStatus === "sending") return false;
	const windowMs = input.network === "telegram" ? ENGAGE_TELEGRAM_EDIT_MS : ENGAGE_WHATSAPP_EDIT_MS;
	return (input.now ?? new Date()).getTime() - new Date(input.createdAt).getTime() <= windowMs;
}

export function canDeleteEngageMessage(input: {
	direction: string;
	deletedAt?: Date | string | null;
	createdAt: Date | string;
	sendStatus?: string | null;
	network?: string | null;
	now?: Date;
}): boolean {
	if (input.network && input.network !== "whatsapp" && input.network !== "telegram") return false;
	if (input.direction !== "outbound" || input.deletedAt) return false;
	if (input.sendStatus === "failed") return true;
	const windowMs = input.network === "telegram" ? ENGAGE_TELEGRAM_DELETE_MS : ENGAGE_WHATSAPP_DELETE_MS;
	return (input.now ?? new Date()).getTime() - new Date(input.createdAt).getTime() <= windowMs;
}

export interface InboundEngageMessage {
	network: EngageInboxNetwork;
	providerAccountId: string;
	providerCustomerId: string;
	customerName: string;
	customerPhone?: string;
	body: string;
	providerMessageId: string | null;
	receivedAt: Date;
	direction?: EngageDirection;
	messageType?: EngageMessageType;
	mediaSha256?: string;
	mediaMime?: string;
	mediaName?: string;
	mediaBase64?: string;
	textEntities?: EngageTextEntity[];
	/** Server-issued Key account id. Required for connector ingest. */
	keyAccountId?: string;
	senderName?: string;
	senderHandle?: string;
	replyTo?: EngageReplyQuote | null;
	kind?: EngageConversationKind;
}

export interface EngageSendInput {
	network: EngageInboxNetwork;
	providerAccountId: string;
	providerCustomerId: string;
	credential: string;
	body: string;
	keyAccountId?: string;
	idempotencyKey?: string;
	media?: { mimeType: string; bytesBase64: string; filename?: string };
	replyTo?: { providerMessageId: string; body?: string; fromMe?: boolean };
}

export interface EngageEditInput {
	network: EngageInboxNetwork;
	providerAccountId: string;
	providerCustomerId: string;
	providerMessageId: string;
	credential: string;
	body: string;
	keyAccountId?: string;
}

export interface EngageRevokeInput {
	network: EngageInboxNetwork;
	providerAccountId: string;
	providerCustomerId: string;
	providerMessageId: string;
	credential: string;
	keyAccountId?: string;
}

export interface EngageReadInput {
	network: EngageInboxNetwork;
	providerAccountId: string;
	providerCustomerId: string;
	credential: string;
	keyAccountId?: string;
	messages: Array<{
		providerMessageId: string;
		chatId?: string;
		senderHandle?: string;
		timestamp?: string;
	}>;
}

export interface EngageFetchMediaInput {
	network: EngageInboxNetwork;
	providerAccountId: string;
	providerCustomerId: string;
	credential: string;
	keyAccountId?: string;
	messages: Array<{ providerMessageId: string }>;
}

export const ENGAGE_LOGIN_STATUSES = [
	"pending",
	"qr",
	"code",
	"connected",
	"failed",
	"expired",
] as const;
export type EngageLoginStatus = (typeof ENGAGE_LOGIN_STATUSES)[number];

export const ENGAGE_LOGIN_METHODS = ["oauth", "qr", "phone_code", "email", "demo", "web"] as const;
export type EngageLoginMethod = (typeof ENGAGE_LOGIN_METHODS)[number];

export interface EngageLoginOptions {
	actorId: string;
	accountId: string;
	nickname?: string;
	consentAcknowledged?: boolean;
	method?: EngageLoginMethod;
	phone?: string;
	email?: string;
}

export interface EngageLogin {
	id: string;
	organisationId: string;
	network: EngageInboxNetwork;
	status: EngageLoginStatus;
	method: EngageLoginMethod;
	loginUrl: string;
	qrValue: string;
	displayName: string;
	providerAccountId: string | null;
	credential: string | null;
	accountId: string | null;
	expiresAt: Date | null;
	hint: string | null;
}

export interface EngageConnectionHealth {
	accountId: string;
	status: EngageAccountStatus;
	detail: string;
}

export interface EngageNetworkCapability {
	network: EngageInboxNetwork;
	status: EngageIntegrationStatus;
	transport: EngageTransport;
	pairing: EngageLoginMethod;
	webInboxUrl: string | null;
	canReceive: boolean;
	canSend: boolean;
	experimental: boolean;
	summary: string;
	blocker: string | null;
}

export const ENGAGE_EVENT_TYPES = [
	"message.received",
	"message.status",
	"login.updated",
	"account.status",
	"conversation.upsert",
	"conversation.deleted",
] as const;
export type EngageEventType = (typeof ENGAGE_EVENT_TYPES)[number];

/**
 * Versioned connector event. organisation_id is resolved from key_account_id
 * on the trusted server mapping and must not be taken from this body.
 */
export interface EngageConnectorEvent {
	version: 1;
	connector_id: string;
	event_id: string;
	key_account_id: string;
	network: EngageInboxNetwork;
	event_type: EngageEventType;
	provider_conversation_id: string;
	provider_message_id: string | null;
	occurred_at: string;
	data: {
		type?: EngageMessageType;
		body?: string;
		customer_name?: string;
		sender_name?: string;
		sender_handle?: string;
		name_trusted?: boolean;
		customer_phone?: string;
		kind?: EngageConversationKind;
		pinned?: boolean;
		participants?: Array<{ provider_id: string; display_name: string; phone?: string; username?: string }>;
		direction?: EngageDirection;
		status?: EngageMessageStatus | EngageAccountStatus | EngageLoginStatus | "revoked" | "edited";
		read_upto?: number;
		login_id?: string;
		display_name?: string;
		provider_account_id?: string;
		media_sha256?: string;
		media_mime?: string;
		media_name?: string;
		media_base64?: string;
		text_entities?: EngageTextEntity[];
		reply_to_provider_id?: string;
		reply_to_name?: string;
		reply_to_body?: string;
	};
}

export type EngageLoginChallengeInteract =
	| { kind: "click"; x: number; y: number }
	| { kind: "type"; text: string }
	| { kind: "key"; key: EngageLoginChallengeKey }
	| { kind: "scroll"; deltaX: number; deltaY: number };

export type EngageLoginChallengeKey =
	| "Enter"
	| "Backspace"
	| "Tab"
	| "Escape"
	| "ArrowUp"
	| "ArrowDown"
	| "ArrowLeft"
	| "ArrowRight"
	| "Delete"
	| "Home"
	| "End";

/**
 * The only surface that may talk to a chat connector. The browser never
 * reaches a bridge process. Conversations stay in Key Postgres, scoped by
 * organisation_id.
 */
export interface EngagePlatform {
	startLogin(
		organisationId: string,
		network: EngageInboxNetwork,
		options?: EngageLoginOptions,
	): Promise<EngageLogin>;
	getLogin(organisationId: string, loginId: string): Promise<EngageLogin>;
	completeLogin(organisationId: string, loginId: string): Promise<EngageLogin>;
	cancelLogin?(organisationId: string, loginId: string): Promise<void>;
	refreshLogin?(organisationId: string, loginId: string): Promise<EngageLogin>;
	submitLoginSecret?(organisationId: string, loginId: string, secret: string): Promise<EngageLogin>;
	challengeImage?(organisationId: string, loginId: string): Promise<Uint8Array>;
	challengeClick?(organisationId: string, loginId: string, x: number, y: number): Promise<EngageLogin>;
	challengeInteract?(organisationId: string, loginId: string, interact: EngageLoginChallengeInteract): Promise<EngageLogin>;
	/** Flush pairing events after the Key account row exists. */
	releaseLogin?(organisationId: string, loginId: string): Promise<void>;
	finishOAuth(query: Record<string, string | undefined>): Promise<EngageLogin>;
	completeOAuth?(login: EngageLogin, query: Record<string, string | undefined>): Promise<EngageLogin>;
	send(input: EngageSendInput): Promise<{ providerMessageId: string }>;
	edit?(input: EngageEditInput): Promise<void>;
	revoke?(input: EngageRevokeInput): Promise<void>;
	read?(input: EngageReadInput): Promise<void>;
	fetchMedia?(input: EngageFetchMediaInput): Promise<void>;
	disconnect(
		organisationId: string,
		providerAccountId: string,
		options?: { keyAccountId?: string; revokeRemoteSession?: boolean },
	): Promise<void>;
	adoptSession?(organisationId: string, fromAccountId: string, toAccountId: string): Promise<void>;
	getConnectionHealth?(organisationId: string, accountId: string): Promise<EngageConnectionHealth>;
	restoreInbox?(input: {
		organisationId: string;
		accountId: string;
		network: EngageInboxNetwork;
		credential?: string;
		providerAccountId?: string;
	}): Promise<EngageConnectionHealth>;
}

export function isEngageInboxNetwork(value: string): value is EngageInboxNetwork {
	return (ENGAGE_INBOX_NETWORKS as readonly string[]).includes(value);
}

export function isEngageLiveNetwork(value: string): value is EngageLiveNetwork {
	return (ENGAGE_LIVE_NETWORKS as readonly string[]).includes(value);
}

/**
 * Messenger and Instagram Page APIs allow a free-form reply for 24 hours after
 * the last inbound message. Linked Devices WhatsApp and Telegram stay open,
 * same as the official web clients.
 */
export function engageReplyWindowOpen(input: {
	network: EngageInboxNetwork;
	lastCustomerMessageAt: Date | null;
	now: Date;
}): boolean {
	if (!(ENGAGE_REPLY_WINDOW_NETWORKS as readonly string[]).includes(input.network)) return true;
	if (!input.lastCustomerMessageAt) return false;
	return input.now.getTime() - input.lastCustomerMessageAt.getTime() <= WINDOW_MS;
}

export const ENGAGE_WEB_INBOX_URLS: Record<EngageInboxNetwork, string> = {
	whatsapp: "https://web.whatsapp.com/",
	telegram: "https://web.telegram.org/",
	messenger: "https://www.messenger.com/",
	instagram: "https://www.instagram.com/direct/inbox/",
	threads: "https://www.threads.net/",
	x: "https://x.com/messages",
	tiktok: "https://www.tiktok.com/messages",
	linkedin: "https://www.linkedin.com/messaging/",
};

/**
 * Product-facing capability matrix. WEB_ONLY is not a unified-inbox integration.
 * WhatsApp Linked Devices and Telegram MTProto are the first real transports.
 */
export function engageNetworkCatalog(): EngageNetworkCapability[] {
	return [
		{
			network: "whatsapp",
			status: "EXPERIMENTAL",
			transport: "whatsapp_web",
			pairing: "qr",
			webInboxUrl: ENGAGE_WEB_INBOX_URLS.whatsapp,
			canReceive: true,
			canSend: true,
			experimental: true,
			summary: "WhatsApp via Linked Devices. Not WhatsApp Business or Cloud API.",
			blocker: "Unofficial WhatsApp Web protocol. Pairing can lead to disconnections, restrictions, or account bans.",
		},
		{
			network: "telegram",
			status: "WORKING",
			transport: "telegram_mtproto",
			pairing: "qr",
			webInboxUrl: ENGAGE_WEB_INBOX_URLS.telegram,
			canReceive: true,
			canSend: true,
			experimental: false,
			summary: "Company Telegram user account via MTProto. Not Bot API.",
			blocker: null,
		},
		{
			network: "messenger",
			status: "SETUP_REQUIRED",
			transport: "meta_page",
			pairing: "oauth",
			webInboxUrl: ENGAGE_WEB_INBOX_URLS.messenger,
			canReceive: true,
			canSend: true,
			experimental: false,
			summary: "Facebook Page Messenger official API. Personal account bridge is not enabled.",
			blocker: "Requires a Meta app, a Page, and messaging permissions. Personal DM bridge is not shipped.",
		},
		{
			network: "instagram",
			status: "SETUP_REQUIRED",
			transport: "meta_page",
			pairing: "oauth",
			webInboxUrl: ENGAGE_WEB_INBOX_URLS.instagram,
			canReceive: true,
			canSend: true,
			experimental: false,
			summary: "Professional Instagram messaging API. Personal DM bridge is not enabled.",
			blocker: "Requires a professional account and Meta messaging permissions.",
		},
		{
			network: "threads",
			status: "BLOCKED",
			transport: "web_only",
			pairing: "web",
			webInboxUrl: ENGAGE_WEB_INBOX_URLS.threads,
			canReceive: false,
			canSend: false,
			experimental: false,
			summary: "Threads has no vetted private-message receive and send API.",
			blocker: "Publishing OAuth is not DM access. Unified inbox for Threads is unfinished.",
		},
		{
			network: "x",
			status: "SETUP_REQUIRED",
			transport: "none",
			pairing: "oauth",
			webInboxUrl: ENGAGE_WEB_INBOX_URLS.x,
			canReceive: false,
			canSend: false,
			experimental: false,
			summary: "X Direct Messages need an approved DM API or a verified bridge.",
			blocker: "OAuth login alone is not DM access. Not connected until a transport is verified.",
		},
		{
			network: "tiktok",
			status: "BLOCKED",
			transport: "web_only",
			pairing: "web",
			webInboxUrl: ENGAGE_WEB_INBOX_URLS.tiktok,
			canReceive: false,
			canSend: false,
			experimental: false,
			summary: "TikTok Login Kit is not a messaging API.",
			blocker: "No vetted personal-DM bridge. Business Messaging is a separate official product.",
		},
		{
			network: "linkedin",
			status: "EXPERIMENTAL",
			transport: "linkedin_voyager",
			pairing: "email",
			webInboxUrl: ENGAGE_WEB_INBOX_URLS.linkedin,
			canReceive: true,
			canSend: true,
			experimental: true,
			summary: "LinkedIn chats in Engage, same inbox as WhatsApp and Telegram.",
			blocker: "LinkedIn may ask for verification. Messages sync through your login session.",
		},
	];
}

export function engageCapability(network: EngageInboxNetwork): EngageNetworkCapability {
	const row = engageNetworkCatalog().find((item) => item.network === network);
	if (!row) {
		return {
			network,
			status: "UNSUPPORTED",
			transport: "none",
			pairing: "web",
			webInboxUrl: null,
			canReceive: false,
			canSend: false,
			experimental: false,
			summary: "That network is not supported.",
			blocker: "Unknown network.",
		};
	}
	return row;
}

/**
 * Inbound events must name a Key-issued account. Matching only on
 * (network, provider account id) is ambiguous across organisations.
 */
export function selectEngageInboundAccount<T extends { id: string; organisationId: string }>(
	matches: readonly T[],
	trustedAccountId: string | undefined,
): T | null {
	if (trustedAccountId) {
		const found = matches.find((row) => row.id === trustedAccountId);
		return found ?? null;
	}
	if (matches.length === 0) return null;
	if (matches.length !== 1) return null;
	return matches[0] ?? null;
}

export function engageInboundAmbiguous(matches: readonly unknown[], trustedAccountId: string | undefined): boolean {
	return !trustedAccountId && matches.length > 1;
}

/** WhatsApp Linked Devices pairing payloads are not Meta OAuth or Key login URLs. */
export function isEngageWhatsAppPairingPayload(value: string): boolean {
	const trimmed = value.trim();
	if (!trimmed) return false;
	if (/^https?:\/\//i.test(trimmed)) {
		try {
			const host = new URL(trimmed).hostname;
			return host === "wa.me" || host === "web.whatsapp.com" || host.endsWith(".whatsapp.net");
		} catch {
			return false;
		}
	}
	return trimmed.includes(",") || trimmed.startsWith("2@");
}
